<!-- Actions toggle (#484): approvals waiting for you; red badge, never cleared by hand. Hidden while the switch is off. -->
@if (actionsEnabled) {
<button
    mat-icon-button
    (click)="openActionsPanel()"
    #actionsOrigin
    [matTooltip]="actions.length ? actions.length + ' action(s) need you' + (overdueCount ? ', ' + overdueCount + ' overdue' : '') : 'No actions'"
>
    @if (actions.length > 0) {
        <span class="absolute left-0 right-0 top-0 flex h-3 items-center justify-center">
            <span
                class="ml-4 mt-2.5 flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-red-600 px-1 text-xs font-medium text-white"
                [ngClass]="{ 'animate-pulse': overdueCount > 0 }"
            >
                {{ actions.length }}
            </span>
        </span>
    }
    <mat-icon [class.text-red-600]="actions.length > 0" [svgIcon]="'heroicons_solid:bell'"></mat-icon>
</button>
}

<!-- Actions panel -->
<ng-template #actionsPanel>
    <div class="fixed inset-0 flex flex-col overflow-hidden shadow-lg sm:static sm:inset-auto sm:w-90 sm:min-w-90 sm:rounded-2xl">
        <div class="flex shrink-0 items-center bg-red-600 py-4 pl-6 pr-4 text-white">
            <div class="-ml-1 mr-3 sm:hidden">
                <button mat-icon-button (click)="closeActionsPanel()">
                    <mat-icon class="text-current icon-size-5" [svgIcon]="'heroicons_solid:x-mark'"></mat-icon>
                </button>
            </div>
            <div class="text-lg font-medium leading-10">Actions</div>
            <div class="ml-auto text-sm opacity-90">Clears when you approve or reject</div>
        </div>
        <div class="bg-card relative flex flex-auto flex-col divide-y overflow-y-auto sm:max-h-120">
            @for (action of actions; track action.id) {
                <a
                    class="flex cursor-pointer flex-col py-4 pl-6 pr-6 hover:bg-gray-50 dark:hover:bg-black dark:hover:bg-opacity-5"
                    [ngClass]="{ 'border-l-4 border-red-600 bg-red-50 dark:bg-red-900/20': action.overdue }"
                    (click)="openAction(action)"
                >
                    <div class="line-clamp-2 font-semibold">{{ action.title }}</div>
                    @if (action.description) {
                        <div class="line-clamp-2">{{ action.description }}</div>
                    }
                    <div class="mt-2 text-sm leading-none" [ngClass]="action.overdue ? 'font-semibold text-red-600' : 'text-secondary'">
                        {{ action.overdue ? 'Overdue since' : 'Due' }} {{ action.dueAt | date: 'MMM dd, HH:mm' }}
                    </div>
                </a>
            }
            @if (!actions.length) {
                <div class="flex flex-auto flex-col items-center justify-center px-8 py-12 sm:justify-start">
                    <div class="mt-2 text-2xl font-semibold tracking-tight">Nothing waiting</div>
                    <div class="text-secondary mt-1 w-full max-w-60 text-center text-md">
                        Approvals that need you will appear here.
                    </div>
                </div>
            }
        </div>
    </div>
</ng-template>

<!-- Notifications toggle -->
<button mat-icon-button (click)="openPanel()" #notificationsOrigin>
    @if (unreadCount > 0) {
        <span
            class="absolute left-0 right-0 top-0 flex h-3 items-center justify-center"
        >
            <span
                class="ml-4 mt-2.5 flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-teal-600 px-1 text-xs font-medium text-indigo-50"
            >
                {{ unreadCount }}
            </span>
        </span>
    }
    <mat-icon [svgIcon]="'heroicons_outline:envelope'"></mat-icon>
</button>

<!-- Notifications panel -->
<ng-template #notificationsPanel>
    <div
        class="fixed inset-0 flex flex-col overflow-hidden shadow-lg sm:static sm:inset-auto sm:w-90 sm:min-w-90 sm:rounded-2xl"
    >
        <!-- Header -->
        <div
            class="flex shrink-0 items-center bg-primary py-4 pl-6 pr-4 text-on-primary"
        >
            <div class="-ml-1 mr-3 sm:hidden">
                <button mat-icon-button (click)="closePanel()">
                    <mat-icon
                        class="text-current icon-size-5"
                        [svgIcon]="'heroicons_solid:x-mark'"
                    ></mat-icon>
                </button>
            </div>
            <div class="text-lg font-medium leading-10">Notifications</div>
            <div class="ml-auto">
                <button
                    class="dark:text-white"
                    mat-icon-button
                    [matTooltip]="'Mark all as read'"
                    [disabled]="unreadCount === 0"
                    (click)="markAllAsRead()"
                >
                    <mat-icon
                        class="text-current icon-size-5"
                        [svgIcon]="'heroicons_solid:envelope-open'"
                    ></mat-icon>
                </button>
            </div>
        </div>

        <!-- Content -->
        <div
            class="bg-card relative flex flex-auto flex-col divide-y overflow-y-auto sm:max-h-120"
        >
            <!-- Notifications -->
            @for (
                notification of notifications;
                track notification.id
            ) {
                <div
                    class="group flex hover:bg-gray-50 dark:hover:bg-black dark:hover:bg-opacity-5"
                    [ngClass]="{ unread: !notification.read }"
                >
                    <!-- Notification with a link: mark read, then open it -->
                    @if (notification.link) {
                        <a
                            class="flex flex-auto cursor-pointer py-5 pl-6"
                            (click)="open(notification)"
                        >
                            <ng-container
                                *ngTemplateOutlet="notificationContent"
                            ></ng-container>
                        </a>
                    }

                    <!-- Notification without a link -->
                    @if (!notification.link) {
                        <div class="flex flex-auto py-5 pl-6">
                          <ng-container
                                *ngTemplateOutlet="notificationContent"
                            ></ng-container>
                        </div>
                    }

                    <!-- Actions -->
                    <div class="relative my-5 ml-2 mr-6 flex flex-col">
                        <!-- Indicator -->
                        <button
                            class="h-6 min-h-6 w-6"
                            mat-icon-button
                            (click)="markRead(notification)"
                            [matTooltip]="notification.read ? 'Read' : 'Mark as read'"
                        >
                            <span
                                class="h-2 w-2 rounded-full"
                                [ngClass]="{
                                    'bg-gray-400 dark:bg-gray-500 sm:opacity-0 sm:group-hover:opacity-100':
                                        notification.read,
                                    'bg-primary': !notification.read,
                                }"
                            ></span>
                        </button>
                        <!-- Remove -->
                        <button
                            class="h-6 min-h-6 w-6 sm:opacity-0 sm:group-hover:opacity-100"
                            mat-icon-button
                            (click)="delete(notification)"
                            [matTooltip]="'Remove'"
                        >
                            <mat-icon
                                class="icon-size-4"
                                [svgIcon]="'heroicons_solid:x-mark'"
                            ></mat-icon>
                        </button>
                    </div>
                </div>

                <!-- Notification content template -->
                <ng-template #notificationContent>
                    <!-- Title, description & time -->
                    <div class="flex flex-auto flex-col">
                        @if (notification.title) {
                            <div class="line-clamp-1 font-semibold" [innerHTML]="notification.title"></div>
                        }
                        @if (notification.description) {
                            <div class="line-clamp-2" [innerHTML]="notification.description"></div>
                        }
                        <div class="text-secondary mt-2 text-sm leading-none">
                            {{ notification.time | date: 'MMM dd yyyy' }}
                        </div>
                    </div>
                </ng-template>
            }

            <!-- No notifications -->
            @if (!notifications || !notifications.length) {
                <div
                    class="flex flex-auto flex-col items-center justify-center px-8 py-12 sm:justify-start"
                >
                    <div
                        class="flex h-14 w-14 flex-0 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-600"
                    >
                        <mat-icon
                            class="text-primary-700 dark:text-primary-50"
                            [svgIcon]="'heroicons_outline:envelope'"
                        ></mat-icon>
                    </div>
                    <div class="mt-5 text-2xl font-semibold tracking-tight">
                        No notifications
                    </div>
                    <div
                        class="text-secondary mt-1 w-full max-w-60 text-center text-md"
                    >
                        When you have notifications, they will appear here.
                    </div>
                </div>
            }
        </div>
    </div>
</ng-template>
